import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO_DURATION_MS = 18000; // ~18 seconds for the intro video

const QUICK_OPTIONS = [
  { icon: '🎧', label: 'Producir mi Audiolibro', message: 'Quiero producir un audiolibro, ¿qué opciones tienen?' },
  { icon: '🎙️', label: 'Crear un Podcast', message: 'Me interesa producir un podcast profesional' },
  { icon: '🔊', label: 'Sonorización / Evento', message: 'Necesito sonorización para un evento en vivo' },
  { icon: '🌐', label: 'Interpretación Simultánea', message: 'Necesito servicio de interpretación simultánea para un evento' },
  { icon: '📡', label: 'Transmisión en Vivo', message: 'Me interesa transmitir un evento en vivo' },
  { icon: '💬', label: 'Ver todos los servicios', message: '¿Qué servicios ofrecen? Quiero conocer todo el catálogo' },
];

export default function AIChatWidget({ onAutoFill, onActivateSections }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [videoPlayed, setVideoPlayed] = useState(false);
  const [showingVideo, setShowingVideo] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const videoTimerRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && !showingVideo) {
      inputRef.current.focus();
    }
  }, [isOpen, showingVideo]);

  // Show bubble hint after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowBubble(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // When chat opens and video hasn't played yet, start video timer
  useEffect(() => {
    if (isOpen && !videoPlayed) {
      setShowingVideo(true);
      const timer = setTimeout(() => {
        setShowingVideo(false);
        setVideoPlayed(true);
      }, VIDEO_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen, videoPlayed]);

  // Parse auto-fill data from AI responses
  const parseAutoFillData = useCallback((text) => {
    const match = text.match(/\[AUTOFILL_COTIZADOR\]([\s\S]*?)\[\/AUTOFILL_COTIZADOR\]/);
    if (match) {
      try {
        return JSON.parse(match[1]);
      } catch (e) {
        return null;
      }
    }
    return null;
  }, []);

  // Parse section activation from AI responses
  const parseActivateSections = useCallback((text) => {
    const match = text.match(/\[ACTIVATE_SECTIONS\]([\s\S]*?)\[\/ACTIVATE_SECTIONS\]/);
    if (match) {
      try {
        const data = JSON.parse(match[1]);
        return data.sections || [];
      } catch (e) {
        return null;
      }
    }
    return null;
  }, []);

  // Clean auto-fill and section activation markers from displayed text
  const cleanAutoFillMarkers = (text) => {
    return text
      .replace(/\[AUTOFILL_COTIZADOR\][\s\S]*?\[\/AUTOFILL_COTIZADOR\]/g, '')
      .replace(/\[ACTIVATE_SECTIONS\][\s\S]*?\[\/ACTIVATE_SECTIONS\]/g, '')
      .trim();
  };

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    // If video is still playing, skip it
    if (showingVideo) {
      if (videoTimerRef.current) clearTimeout(videoTimerRef.current);
      setShowingVideo(false);
      setVideoPlayed(true);
    }

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setHasStarted(true);

    try {
      const res = await axios.post(`${API}/chat/message`, {
        message: text,
        session_id: sessionId
      });
      if (res.data.session_id && !sessionId) {
        setSessionId(res.data.session_id);
      }
      
      const responseText = res.data.response;
      
      // Check for section activation
      const sectionsToActivate = parseActivateSections(responseText);
      if (sectionsToActivate && sectionsToActivate.length > 0 && onActivateSections) {
        setTimeout(() => onActivateSections(sectionsToActivate), 300);
      }

      // Check for auto-fill data
      const autoFillData = parseAutoFillData(responseText);
      if (autoFillData && onAutoFill) {
        // Execute auto-fill with slight delay for visual effect
        setTimeout(() => onAutoFill(autoFillData), 500);
      }
      
      // Clean the response before displaying
      const cleanText = cleanAutoFillMarkers(responseText);
      setMessages(prev => [...prev, { role: 'assistant', content: cleanText }]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Lo siento, hubo un error. Por favor intenta de nuevo o usa el cotizador interactivo de la página.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickOption = (option) => {
    if (showingVideo) {
      if (videoTimerRef.current) clearTimeout(videoTimerRef.current);
      setShowingVideo(false);
      setVideoPlayed(true);
    }
    if (option.message) {
      sendMessage(option.message);
    } else {
      setHasStarted(true);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleOpenChat = () => {
    setIsOpen(true);
    setShowBubble(false);
  };

  const formatMessage = (text) => {
    let html = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/^[•\-]\s/gm, '&bull; ');
    html = html.replace(/\n/g, '<br/>');
    return html;
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50 flex items-end gap-3" data-testid="ai-chat-bubble">
          {showBubble && (
            <div
              className="bg-white rounded-2xl rounded-br-md shadow-2xl p-3 max-w-[220px] cursor-pointer animate-fade-in"
              onClick={handleOpenChat}
              style={{ animation: 'fadeInUp 0.4s ease-out' }}
            >
              <p className="text-gray-800 text-sm font-medium leading-snug">
                ¿Quieres que te ayude a cotizar de manera inteligente?
              </p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-gray-500">Mr. Edux · Asistente Viubux</span>
              </div>
            </div>
          )}

          <button
            onClick={handleOpenChat}
            className="w-16 h-16 rounded-full shadow-2xl overflow-hidden border-2 border-[#272b96] hover:border-[#aa2aff] transition-all hover:scale-110 bg-white"
            data-testid="btn-open-ai-chat"
          >
            <img src="/agent-avatar.png" alt="Mr. Edux" className="w-full h-full object-cover object-top" />
          </button>
        </div>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[380px] h-[540px] flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-white/20"
          style={{ animation: 'fadeInUp 0.3s ease-out' }}
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#272b96] to-[#aa2aff] px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/40 flex-shrink-0 bg-white">
              <img src="/agent-avatar.png" alt="Mr. Edux" className="w-full h-full object-cover object-top" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-sm">Mr. Edux · Asistente Viubux</h3>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-white/70 text-xs">En línea</span>
              </div>
            </div>
            <button
              onClick={() => {
                if (showingVideo && videoTimerRef.current) clearTimeout(videoTimerRef.current);
                if (showingVideo) { setShowingVideo(false); setVideoPlayed(true); }
                setIsOpen(false);
              }}
              className="text-white/70 hover:text-white transition-colors p-1"
              data-testid="btn-close-ai-chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Video Intro - fullscreen in chat */}
          {showingVideo && (
            <div className="flex-1 bg-black flex items-center justify-center relative">
              <iframe
                src="https://share.descript.com/embed/AX5bULXy6kg?autoplay=1"
                width="100%"
                height="100%"
                frameBorder="0"
                allow="autoplay; fullscreen; accelerometer"
                allowFullScreen
                style={{ border: 'none' }}
                title="Bienvenida Mr. Edux"
              />
              {/* Progress bar showing video time remaining */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <div 
                  className="h-full bg-gradient-to-r from-[#aa2aff] to-[#272b96]"
                  style={{ animation: `videoProgress ${VIDEO_DURATION_MS}ms linear forwards` }}
                />
              </div>
              <button
                onClick={() => {
                  setShowingVideo(false);
                  setVideoPlayed(true);
                }}
                className="absolute top-3 right-3 text-white/80 hover:text-white text-xs bg-black/60 rounded-full px-3 py-1.5 backdrop-blur-sm border border-white/20 hover:border-white/40 transition-all"
                data-testid="btn-skip-video"
              >
                Saltar Intro
              </button>
              <style>{`
                @keyframes videoProgress {
                  from { width: 0%; }
                  to { width: 100%; }
                }
              `}</style>
            </div>
          )}

          {/* Messages Area - only show when not playing video */}
          {!showingVideo && (
            <>
              <div className="flex-1 overflow-y-auto bg-gradient-to-b from-[#0a0e2a] to-[#0f133a] p-4 space-y-3">
                {/* Welcome message (after video or if video already played) */}
                {!hasStarted && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <div className="w-7 h-7 rounded-full overflow-hidden border border-[#aa2aff]/30 flex-shrink-0 bg-white mt-0.5">
                        <img src="/agent-avatar.png" alt="Mr. Edux" className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="bg-white/10 backdrop-blur rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%]">
                        <p className="text-white text-sm leading-relaxed">
                          ¡Hola! Soy <strong className="text-[#aa2aff]">Mr. Edux</strong>, tu asistente de cotizaciones en Viubux.
                        </p>
                        <p className="text-slate-300 text-sm mt-2">
                          ¿En qué te puedo ayudar hoy?
                        </p>
                      </div>
                    </div>

                    {/* Quick action buttons */}
                    <div className="pl-9 space-y-2">
                      {QUICK_OPTIONS.map((opt, i) => (
                        <button
                          key={i}
                          onClick={() => handleQuickOption(opt)}
                          className="w-full text-left bg-white/5 hover:bg-[#aa2aff]/20 border border-white/10 hover:border-[#aa2aff]/40 rounded-xl px-3 py-2.5 text-sm text-slate-200 hover:text-white transition-all flex items-center gap-2"
                          data-testid={`chat-quick-option-${i}`}
                        >
                          <span className="text-base">{opt.icon}</span>
                          <span>{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chat messages */}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-full overflow-hidden border border-[#aa2aff]/30 flex-shrink-0 bg-white mt-0.5">
                        <img src="/agent-avatar.png" alt="Mr. Edux" className="w-full h-full object-cover object-top" />
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2.5 max-w-[85%] text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-[#272b96] to-[#aa2aff] text-white rounded-tr-md'
                          : 'bg-white/10 backdrop-blur text-slate-100 rounded-tl-md'
                      }`}
                    >
                      {msg.role === 'assistant' ? (
                        <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className="flex gap-2">
                    <div className="w-7 h-7 rounded-full overflow-hidden border border-[#aa2aff]/30 flex-shrink-0 bg-white mt-0.5">
                      <img src="/agent-avatar.png" alt="Mr. Edux" className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-2xl rounded-tl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 text-[#aa2aff] animate-spin" />
                        <span className="text-slate-400 text-sm">Mr. Edux está pensando...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <form
                onSubmit={handleSubmit}
                className="bg-[#0a0e2a] border-t border-white/10 px-3 py-3 flex items-center gap-2"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 bg-white/10 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#aa2aff]/50 transition-colors"
                  data-testid="input-ai-chat"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className="p-2.5 bg-gradient-to-r from-[#272b96] to-[#aa2aff] rounded-xl text-white disabled:opacity-30 hover:shadow-lg hover:shadow-[#aa2aff]/20 transition-all"
                  data-testid="btn-send-ai-chat"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* Global animation styles */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
